import AsyncStorage from '@react-native-async-storage/async-storage'
import { Breadcrumb } from '@sentry/browser'
import * as crypto from 'expo-crypto'
import { Platform } from 'react-native'
import * as Sentry from 'sentry-expo'

import { API_URL, ENVIRONMENT, SENTRY_DSN, VERSION } from '../constants/config'

const ENABLE_SENTRY = ['dev', 'dev-vibe', 'staging', 'uat', 'uat-vibe', 'prod', 'prod-vibe'].includes(ENVIRONMENT)
const ENABLE_SENTRY_DEBUG = ['dev', 'dev-vibe', 'staging', 'uat', 'uat-vibe'].includes(ENVIRONMENT)

export function initSentry() {
  if (!ENABLE_SENTRY) {
    return
  }
  if (Platform.OS === 'web') {
    Sentry.Browser.init({
      dsn: SENTRY_DSN,
      release: VERSION,
      environment: ENVIRONMENT,
      debug: ENABLE_SENTRY_DEBUG,
      integrations: [
        new Sentry.Browser.BrowserTracing({
          tracePropagationTargets: [API_URL, /^\//],
        }),
        new Sentry.Browser.Replay({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
        }),
      ],
      tracesSampleRate: ENVIRONMENT === 'prod-vibe' ? 0.3 : 0.5,
      // Do not record the entire user session, only capture a replay when an error occurs
      replaysSessionSampleRate: 0.5,
      replaysOnErrorSampleRate: 1.0,
    })
  } else {
    Sentry.init({
      dsn: SENTRY_DSN,
      release: VERSION,
      environment: ENVIRONMENT,
      debug: ENABLE_SENTRY_DEBUG,
      // To enable while in development see https://docs.expo.dev/guides/using-sentry/#expo-dev-client-transactions-never-finish
      // https://github.com/getsentry/sentry-react-native/issues/2721 and https://github.com/expo/sentry-expo/issues/368
      enableInExpoDevelopment: false,
      tracesSampleRate: ENVIRONMENT === 'prod-vibe' ? 0.3 : 1,
    })
  }

  startSession()
}

const BREADCRUMB_CATEGORY: Breadcrumb['category'] = 'log'
const BREADCRUMB_LEVEL: Breadcrumb['level'] = 'log'

type BreadcrumbOptions = {
  level?: Breadcrumb['level']
  data?: Breadcrumb['data']
}

export function addSentryBreadcrumb(
  message: string,
  options: BreadcrumbOptions = {
    level: BREADCRUMB_LEVEL,
    data: undefined,
  }
) {
  const { level = BREADCRUMB_LEVEL, data = undefined }: BreadcrumbOptions = options
  if (!ENABLE_SENTRY) {
    return
  }
  if (Platform.OS === 'web') {
    Sentry.Browser.addBreadcrumb({
      category: BREADCRUMB_CATEGORY,
      message,
      level,
      data,
    })
  } else {
    Sentry.Native.addBreadcrumb({
      category: BREADCRUMB_CATEGORY,
      message,
      level,
      data,
    })
  }
}

async function startSession() {
  const sessionId = crypto.randomUUID().toLowerCase()

  if ((await AsyncStorage.getItem('sessionId')) === null) {
    await AsyncStorage.setItem('sessionId', sessionId)
  }
  if (Platform.OS === 'web') {
    Sentry.Browser.setContext('session', { sessionId })
    window.cardoTrace = () => sessionId
  } else {
    Sentry.Native.setContext('session', { sessionId })
  }
}

export async function endSession() {
  await AsyncStorage.removeItem('sessionId')
  return startSession()
}

export async function getSessionId(): Promise<string> {
  return (await AsyncStorage.getItem('sessionId')) ?? ''
}

export function getTraceId() {
  /* TODO: investigate how to use Sentry's tracing API to generate a traceId
      - Sentry.Browser.startTransaction({ name: 'request' })
      - Sentry.Browser.setContext('trace', { traceId: crypto.randomUUID() })
      - Sentry.Browser.setTag('traceId', crypto.randomUUID())
  */

  return crypto.randomUUID().toLowerCase()
}

declare global {
  interface Window {
    cardoTrace: () => string
  }
}
